// Read-only view of the active PatientInfo + the patient's correction-
// request workflow. Identity fields (name, DOB, gender, mobile, email)
// are clinic-owned; the patient files a correction request that the
// support / clinic team reviews via the admin endpoints in
// AdminCorrectionRequestsController.

import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { usePatient } from '../../context/PatientContext'
import {
  BackLink,
  Card,
  CardTitle,
  ContentMaxWidth,
  GhostButton,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  PrimaryButton,
  Row,
  Stack,
} from '../../layout/styledComponents'
import {
  cancelCorrectionRequest,
  listMyCorrectionRequests,
} from '../../services/correctionRequestsService'
import {
  DIETARY_PREFERENCE_OPTIONS,
  getPreferences,
  updatePreferences,
} from '../../services/preferencesService'
import { CorrectionRequestsSkeleton } from '../../components/Skeleton'
import CorrectionRequestDialog from './components/CorrectionRequestDialog'

// Toggle chip for the dietary-preference picker. Selected chip uses
// theme primary tone; others read as quiet outlined buttons. Tapping
// selects + persists immediately (no separate Save button — the patient
// flips between options often, so an inline save matches the UX).
const DietaryChip = styled.button`
  appearance: none;
  border: 1px solid
    ${(p) =>
      p.$active
        ? p.theme?.color?.primary || '#1F3A5F'
        : p.theme?.color?.border || '#E6E0D6'};
  background: ${(p) =>
    p.$active
      ? p.theme?.color?.primarySoft || '#E8EEF6'
      : p.theme?.color?.bgRaised || '#fff'};
  color: ${(p) =>
    p.$active
      ? p.theme?.color?.primary || '#1F3A5F'
      : p.theme?.color?.inkSoft || '#4B5563'};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 150ms ease-out;
  &:hover:not(:disabled) {
    background: ${(p) => p.theme?.color?.primarySoft || '#E8EEF6'};
    border-color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const FIELD_LABEL = {
  name: 'Name',
  dateOfBirth: 'Date of birth',
  gender: 'Gender',
  mobile: 'Mobile',
  email: 'Email',
  other: 'Other',
}

const STATUS_TONE = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'urgent',
  CANCELLED: 'primary',
}

function formatRequestSummary(req) {
  const fld = FIELD_LABEL[req.fieldName] || req.fieldName || 'Field'
  if (req.fieldName === 'other') return fld
  if (req.requestedValue) return `${fld} → "${req.requestedValue}"`
  return fld
}

export default function PersonalProfilePage() {
  const { currentProfile, refreshProfiles } = usePatient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [requests, setRequests] = useState([])
  const [requestsLoading, setRequestsLoading] = useState(true)
  // Patient-portal-owned preferences (dietary preference + future
  // patient-driven settings). Backed by /api/secure/me/preferences.
  const [preferences, setPreferences] = useState(null)
  const [prefsLoading, setPrefsLoading] = useState(true)
  const [prefsSaving, setPrefsSaving] = useState(false)
  const [prefsError, setPrefsError] = useState(null)

  const reloadRequests = useCallback(async () => {
    if (!currentProfile?.id) {
      setRequests([])
      setRequestsLoading(false)
      return
    }
    setRequestsLoading(true)
    try {
      // Listing returns the user's full request history. Filter client-side
      // to the active patient — the backend doesn't support a per-patient
      // filter on this endpoint yet (would be a follow-up).
      const all = await listMyCorrectionRequests({ pageSize: 50 })
      setRequests(
        Array.isArray(all)
          ? all.filter((r) => r.patientId === currentProfile.id)
          : [],
      )
    } catch {
      setRequests([])
    } finally {
      setRequestsLoading(false)
    }
  }, [currentProfile?.id])

  useEffect(() => {
    reloadRequests()
  }, [reloadRequests])

  useEffect(() => {
    let cancelled = false
    if (!currentProfile?.id) {
      setPreferences(null)
      setPrefsLoading(false)
      return () => {}
    }
    setPrefsLoading(true)
    getPreferences(currentProfile)
      .then((p) => {
        if (cancelled) return
        setPreferences(p || null)
      })
      .catch(() => {
        if (!cancelled) setPreferences(null)
      })
      .finally(() => {
        if (!cancelled) setPrefsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [currentProfile])

  async function handleDietaryChange(nextCode) {
    if (!currentProfile) return
    setPrefsError(null)
    setPrefsSaving(true)
    try {
      const updated = await updatePreferences(currentProfile, {
        // Backend treats empty-string as "clear" (Jackson absent-vs-null
        // ambiguity); send '' when the user picks "No preference".
        dietaryPreference: nextCode == null ? '' : nextCode,
      })
      setPreferences(updated)
      // Also refresh profile listing so the dietaryPreference echoed on
      // PatientProfileResponse updates immediately for FoodLogDialog
      // (which reads currentProfile.dietaryPreference).
      if (refreshProfiles) refreshProfiles()
    } catch (e) {
      setPrefsError('Could not save dietary preference. Try again in a minute.')
    } finally {
      setPrefsSaving(false)
    }
  }

  async function handleCancel(req) {
    if (req.status !== 'PENDING') return
    setRequests((prev) =>
      prev.map((r) => (r.id === req.id ? { ...r, status: 'CANCELLED' } : r)),
    )
    try {
      await cancelCorrectionRequest(req.id)
    } catch {
      reloadRequests()
    }
  }

  if (!currentProfile) return null

  const pendingCount = requests.filter((r) => r.status === 'PENDING').length

  return (
    <ContentMaxWidth>
      <Stack $gap="24px">
        <BackLink to="/patient-home/profile">Back to Profile</BackLink>
        <div>
          <Pill>Profile</Pill>
          <PageHeading style={{ marginTop: 12 }}>Personal details</PageHeading>
          <PageSubheading>
            Identity fields (name, date of birth, gender, mobile, email) are owned by your clinic.
            If something looks wrong, raise a correction request below — the clinic / support team
            will review and get back to you.
          </PageSubheading>
        </div>

        <Card>
          <Row $between $gap="12px" $wrap>
            <CardTitle style={{ margin: 0 }}>{currentProfile.displayName}</CardTitle>
            <PrimaryButton onClick={() => setDialogOpen(true)}>Request a correction</PrimaryButton>
          </Row>
          <Stack $gap="6px" style={{ marginTop: 16 }}>
            <Muted>
              <strong>Brand:</strong> {currentProfile.brandName || '—'}
            </Muted>
            {currentProfile.patientNumber && (
              <Muted>
                <strong>Patient number:</strong> {currentProfile.patientNumber}
              </Muted>
            )}
            <Muted>
              <strong>Relation:</strong> {currentProfile.relation || 'OTHER'}
            </Muted>
            {currentProfile.dob && (
              <Muted>
                <strong>Date of birth:</strong> {currentProfile.dob}
              </Muted>
            )}
            {currentProfile.gender && (
              <Muted>
                <strong>Gender:</strong> {currentProfile.gender}
              </Muted>
            )}
            <Muted>
              <strong>Allergies:</strong>{' '}
              {currentProfile.allergies?.length
                ? currentProfile.allergies.join(', ')
                : 'None recorded'}
            </Muted>
            {currentProfile.medicalHistory && (
              <Muted>
                <strong>Medical history:</strong> {currentProfile.medicalHistory}
              </Muted>
            )}
          </Stack>
        </Card>

        <Card>
          <CardTitle style={{ margin: 0 }}>Preferences</CardTitle>
          <Muted style={{ marginTop: 6, marginBottom: 14 }}>
            Personal preferences you control directly. Used by features like the meal-log
            picker — set to vegetarian and the catalog suggestions filter to veg by default.
            You can still log anything off-diet (the picker has a "Show all" toggle and
            free-text always works).
          </Muted>
          <Stack $gap="10px">
            <div>
              <strong style={{ fontSize: 13 }}>Dietary preference</strong>
            </div>
            <Row $gap="8px" $wrap>
              <DietaryChip
                $active={!preferences?.dietaryPreference}
                disabled={prefsLoading || prefsSaving}
                onClick={() => handleDietaryChange(null)}
              >
                {preferences?.dietaryPreference ? '○' : '✓'} No preference
              </DietaryChip>
              {DIETARY_PREFERENCE_OPTIONS.map((opt) => {
                const active = preferences?.dietaryPreference === opt.code
                return (
                  <DietaryChip
                    key={opt.code}
                    $active={active}
                    disabled={prefsLoading || prefsSaving}
                    onClick={() => handleDietaryChange(opt.code)}
                  >
                    <span aria-hidden style={{ marginRight: 6 }}>{opt.emoji}</span>
                    {opt.label}
                  </DietaryChip>
                )
              })}
            </Row>
            {prefsError ? (
              <Muted style={{ color: '#C24D49' }}>{prefsError}</Muted>
            ) : prefsSaving ? (
              <Muted>Saving…</Muted>
            ) : null}
          </Stack>
        </Card>

        <Card>
          <Row $between $gap="8px" $wrap>
            <CardTitle style={{ margin: 0 }}>Your correction requests</CardTitle>
            {pendingCount > 0 ? <Pill $tone="warning">{pendingCount} pending</Pill> : null}
          </Row>
          {requestsLoading ? (
            <CorrectionRequestsSkeleton />
          ) : requests.length === 0 ? (
            <Muted style={{ marginTop: 12 }}>
              You haven't raised any corrections for this profile yet.
            </Muted>
          ) : (
            <Stack $gap="10px" style={{ marginTop: 16 }}>
              {requests.map((req) => (
                <div
                  key={req.id}
                  style={{
                    padding: 12,
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: 12,
                    background: '#fff',
                  }}
                >
                  <Row $between $gap="8px" $wrap>
                    <Stack $gap="4px" style={{ minWidth: 0, flex: 1 }}>
                      <strong style={{ color: '#1F2937', fontSize: 14 }}>
                        {formatRequestSummary(req)}
                      </strong>
                      {req.reason ? (
                        <Muted style={{ fontSize: 13 }}>"{req.reason}"</Muted>
                      ) : null}
                      {req.reviewerNote ? (
                        <Muted style={{ fontSize: 13 }}>
                          <strong>Reviewer:</strong> {req.reviewerNote}
                        </Muted>
                      ) : null}
                    </Stack>
                    <Stack $gap="6px" style={{ alignItems: 'flex-end' }}>
                      <Pill $tone={STATUS_TONE[req.status] || 'primary'}>{req.status}</Pill>
                      {req.status === 'PENDING' ? (
                        <GhostButton
                          onClick={() => handleCancel(req)}
                          style={{ padding: '4px 12px', fontSize: 12 }}
                        >
                          Cancel
                        </GhostButton>
                      ) : null}
                    </Stack>
                  </Row>
                </div>
              ))}
            </Stack>
          )}
        </Card>
      </Stack>

      <CorrectionRequestDialog
        open={dialogOpen}
        profile={currentProfile}
        onClose={() => setDialogOpen(false)}
        onSubmitted={reloadRequests}
      />
    </ContentMaxWidth>
  )
}
